import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { obtenerDatosTenantPDF } from "./tenantPDF";
import { fechaHoy } from "./internacional";

// ─── Paleta de colores ───────────────────────────────────────────────────────
const PALETTE = {
  dashDark:   [30, 58, 138],   // #1E3A8A Blue 900
  dashLight:  [59, 130, 246],  // #3B82F6 Blue 500
  dashBanda:  [239, 246, 255], // #EFF6FF Blue 50
  success:    [22, 163, 74],
  danger:     [220, 38, 38],
  dark:       [33, 37, 41],
  grayText:   [75, 85, 99],
  grayLabel:  [140, 145, 155],
  hairline:   [229, 231, 235],
};

const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;
const soloFecha = (f) => {
  if (!f) return "-";
  const s = String(f).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;
};

const procesarImagen = (src, width = 150, calidad = 1.0) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const escala = width / img.width;
      canvas.width = width;
      canvas.height = img.height * escala;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/png", calidad));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const nombreMes = (k) => {
  const [y, m] = (k || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString("es-CO", { month: "short", year: "numeric" });
};

const limpiar = (txt) => String(txt || "").replace(/[\u{1F000}-\u{1FFFF}]|[\u{2600}-\u{27BF}]|[\u{FE00}-\u{FE0F}]|[\u{1F900}-\u{1F9FF}]|[\u{200D}]|[\u{20E3}]|[\u{E0020}-\u{E007F}]/gu, "").trim();

export async function generarPDFDashboard(data) {
  const {
    desde, hasta, periodoTexto,
    kpis, serieMes, topClientes,
    articulosPropios, articulosProveedor, proveedores,
    resumenFinanciero, resumenClientes, resumenProveedoresTexto,
  } = data;

  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  let emp = { nombre: "Mi Empresa", direccion: "", telefono: "", logoUrl: null, fondoUrl: null };
  let logo = null, fondo = null;

  try {
    const datosTenant = await obtenerDatosTenantPDF();
    if (datosTenant) emp = datosTenant; 
    logo = emp.logoUrl ? await procesarImagen(emp.logoUrl, 250, 1.0) : null;
    fondo = emp.fondoUrl ? await procesarImagen(emp.fondoUrl, 300, 0.9) : null;
  } catch (error) {
    console.error("Error cargando recursos PDF:", error);
  }

  const insertarFondo = () => {
    if (!fondo) return;
    const cx = (pageW - 150) / 2, cy = (pageH - 150) / 2;
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.addImage(fondo, "PNG", cx, cy, 150, 150);
    doc.restoreGraphicsState();
  };
  insertarFondo();

  const T = { dark: PALETTE.dashDark, light: PALETTE.dashLight, banda: PALETTE.dashBanda };
  const MARGIN_X = 14;
  const MARGIN_TOP = 10;
  const rightMarginX = pageW - MARGIN_X;

  const bandaH = 26;
  doc.setFillColor(...T.banda);
  doc.rect(0, MARGIN_TOP, pageW, bandaH, "F");
  doc.setFillColor(...T.dark);
  doc.rect(0, MARGIN_TOP, pageW, 1.2, "F");

  if (logo) {
    const logoSize = 32;
    const logoX = MARGIN_X;
    const logoY = MARGIN_TOP + 4;
    doc.setFillColor(255, 255, 255);
    doc.circle(logoX + logoSize / 2, logoY + logoSize / 2, (logoSize / 2) + 1.5, "F");
    doc.addImage(logo, "PNG", logoX, logoY, logoSize, logoSize);
  }

  const infoX = MARGIN_X + 38;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...PALETTE.dark);
  doc.text(emp.nombre || "", infoX, MARGIN_TOP + 11);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...PALETTE.grayText);
  doc.text(emp.direccion || "", infoX, MARGIN_TOP + 16);
  doc.text(emp.telefono ? `Cel-Whatsapp ${emp.telefono}` : "", infoX, MARGIN_TOP + 20.5);

  const periodoLabel = periodoTexto || `${soloFecha(desde)} - ${soloFecha(hasta)}`;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  doc.setTextColor(100, 110, 120);
  doc.text("DASHBOARD FINANCIERO", rightMarginX, MARGIN_TOP + 13.5, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...PALETTE.grayText);
  doc.text(`Período: ${limpiar(periodoLabel)}`, rightMarginX, MARGIN_TOP + 18.5, { align: "right" });

  let y = MARGIN_TOP + 42;

  const drawSectionTitle = (title, currentY) => {
    if (currentY + 20 > pageH - 22) { doc.addPage(); insertarFondo(); currentY = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...T.dark);
    doc.text(limpiar(title), MARGIN_X, currentY);
    doc.setDrawColor(...T.light);
    doc.setLineWidth(0.4);
    const tw = doc.getTextWidth(limpiar(title));
    doc.line(MARGIN_X, currentY + 2, MARGIN_X + tw, currentY + 2);
    return currentY + 8;
  };

  const drawSmallText = (lines, currentY) => {
    if (!lines || !lines.length) return currentY;
    doc.setFontSize(9.5);
    doc.setTextColor(...PALETTE.grayText);
    lines.forEach((line) => {
      if (currentY + 10 > pageH - 22) { doc.addPage(); insertarFondo(); currentY = 20; }
      const cleaned = limpiar(line);
      if (!cleaned) return;
      const wrapped = doc.splitTextToSize(cleaned, pageW - (MARGIN_X * 2));
      doc.text(wrapped, MARGIN_X, currentY);
      currentY += wrapped.length * 4.2;
    });
    return currentY + 2;
  };

  const applyHeaderGradient = (data) => {
    if (data.section === "head") {
      const { x, y, width, height } = data.cell;
      const steps = Math.max(20, Math.ceil(width));
      const stripW = width / steps;
      for (let i = 0; i < steps; i++) {
        const localX = i * stripW;
        const globalX = x + localX - MARGIN_X;
        const tableW = pageW - (MARGIN_X * 2);
        const t = Math.max(0, Math.min(1, globalX / tableW));
        const r = Math.round(T.dark[0] + t * (T.light[0] - T.dark[0]));
        const g = Math.round(T.dark[1] + t * (T.light[1] - T.dark[1]));
        const b = Math.round(T.dark[2] + t * (T.light[2] - T.dark[2]));
        doc.setFillColor(r, g, b);
        doc.rect(x + localX, y, stripW + 0.2, height, "F");
      }
    }
  };

  const commonTableStyles = {
    styles: { font: "helvetica", fontSize: 9.5, cellPadding: 1.5, textColor: PALETTE.dark },
    headStyles: { fillColor: false, textColor: 255, halign: "center", valign: "middle", fontStyle: "bold", fontSize: 9.5, minCellHeight: 9 },
    margin: { left: MARGIN_X, right: MARGIN_X },
    willDrawCell: applyHeaderGradient,
    didDrawPage: insertarFondo,
  };

  // KPIs
  y = drawSectionTitle("Indicadores Clave (KPIs)", y);
  autoTable(doc, {
    ...commonTableStyles,
    startY: y,
    head: [["Ingresos Reales", "Gastos Reales", "Ganancia Neta", "Pedidos con Abono"]],
    body: [[ money(kpis.ingresos), money(kpis.gastos), money(kpis.ganancia), String(kpis.pedidos || 0) ]],
    columnStyles: {
      0: { halign: "center", fontStyle: "bold", textColor: PALETTE.success, fontSize: 11 },
      1: { halign: "center", fontStyle: "bold", textColor: PALETTE.danger, fontSize: 11 },
      2: { halign: "center", fontStyle: "bold", textColor: (kpis.ganancia >= 0 ? PALETTE.success : PALETTE.danger), fontSize: 11 },
      3: { halign: "center", fontStyle: "bold", textColor: PALETTE.grayText, fontSize: 11 },
    },
    didParseCell: (data) => { if (data.section === "body") data.cell.styles.fillColor = [255, 255, 255]; }
  });
  y = doc.lastAutoTable.finalY + 4;

  const vars = [];
  if (kpis.pctIng !== undefined && kpis.pctIng !== 0) vars.push(`Ingresos: ${kpis.pctIng > 0 ? "+" : ""}${kpis.pctIng}% vs anterior`);
  if (kpis.pctGas !== undefined && kpis.pctGas !== 0) vars.push(`Gastos: ${kpis.pctGas > 0 ? "+" : ""}${kpis.pctGas}% vs anterior`);
  if (vars.length) {
    doc.setFontSize(9);
    doc.setTextColor(...PALETTE.grayLabel);
    doc.text(vars.join("   |   "), MARGIN_X, y);
    y += 8;
  }

  if (resumenFinanciero && resumenFinanciero.length) {
    y = drawSectionTitle("Resumen Financiero", y);
    y = drawSmallText(resumenFinanciero, y) + 4;
  }

  // Financiero Mes a Mes
  if (serieMes && serieMes.meses && serieMes.meses.length > 0) {
    y = drawSectionTitle("Tendencia Financiera por Mes", y);
    let zebraIndex = 0;
    autoTable(doc, {
      ...commonTableStyles,
      startY: y,
      head: [["Mes", "Ingresos", "Gastos", "Ganancia"]],
      body: serieMes.meses.map((k, i) => [
        nombreMes(k), money(serieMes.ingresos[i]), money(serieMes.gastos[i]), money(serieMes.ganancia[i]),
      ]),
      columnStyles: {
        1: { halign: "right", textColor: PALETTE.success },
        2: { halign: "right", textColor: PALETTE.danger },
        3: { halign: "right", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "body") {
          const shade = zebraIndex % 2 === 0 ? 255 : 248;
          data.cell.styles.fillColor = [shade, shade, shade];
          if (data.column.index === data.table.columns.length - 1) zebraIndex++;
        }
      }
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Clientes
  if (topClientes && topClientes.length > 0) {
    y = drawSectionTitle("Top Clientes - Recaudo Real", y);
    let zebraIndex = 0;
    autoTable(doc, {
      ...commonTableStyles,
      startY: y,
      head: [["#", "Cliente", "Pedidos", "Recaudo Real", "Promedio"]],
      body: topClientes.map((c, i) => [ i + 1, limpiar(c.nombre), c.pedidos, money(c.recaudo), money(c.promedio) ]),
      columnStyles: {
        0: { cellWidth: 12, halign: "center" },
        2: { halign: "center", cellWidth: 22 },
        3: { halign: "right", fontStyle: "bold", textColor: PALETTE.success },
        4: { halign: "right", textColor: PALETTE.grayText },
      },
      didParseCell: (data) => {
        if (data.section === "body") {
          const shade = zebraIndex % 2 === 0 ? 255 : 248;
          data.cell.styles.fillColor = [shade, shade, shade];
          if (data.column.index === data.table.columns.length - 1) zebraIndex++;
        }
      }
    });
    y = doc.lastAutoTable.finalY + 4;
    if (resumenClientes && resumenClientes.length) y = drawSmallText(resumenClientes, y) + 4;
  }

  // Articulos Propios
  if (articulosPropios && articulosPropios.length > 0) {
    y = drawSectionTitle("Artículos Más Alquilados (Propios)", y);
    let zebraIndex = 0;
    autoTable(doc, {
      ...commonTableStyles,
      startY: y,
      head: [["#", "Artículo", "Cantidad"]],
      body: articulosPropios.slice(0, 10).map((a, i) => [ i + 1, limpiar(a.nombre), Number(a.cantidad).toLocaleString("es-CO") ]),
      columnStyles: { 0: { cellWidth: 12, halign: "center" }, 2: { halign: "right", fontStyle: "bold" } },
      didParseCell: (data) => {
        if (data.section === "body") {
          const shade = zebraIndex % 2 === 0 ? 255 : 248;
          data.cell.styles.fillColor = [shade, shade, shade];
          if (data.column.index === data.table.columns.length - 1) zebraIndex++;
        }
      }
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Articulos Proveedor
  if (articulosProveedor && articulosProveedor.length > 0) {
    y = drawSectionTitle("Artículos Más Alquilados (Proveedor)", y);
    let zebraIndex = 0;
    autoTable(doc, {
      ...commonTableStyles,
      startY: y,
      head: [["#", "Artículo", "Cantidad"]],
      body: articulosProveedor.slice(0, 10).map((a, i) => [ i + 1, limpiar(a.nombre), Number(a.cantidad).toLocaleString("es-CO") ]),
      columnStyles: { 0: { cellWidth: 12, halign: "center" }, 2: { halign: "right", fontStyle: "bold" } },
      didParseCell: (data) => {
        if (data.section === "body") {
          const shade = zebraIndex % 2 === 0 ? 255 : 248;
          data.cell.styles.fillColor = [shade, shade, shade];
          if (data.column.index === data.table.columns.length - 1) zebraIndex++;
        }
      }
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Proveedores
  if (proveedores && proveedores.length > 0) {
    y = drawSectionTitle("Resumen de Proveedores - Pagos Reales", y);
    let zebraIndex = 0;
    autoTable(doc, {
      ...commonTableStyles,
      startY: y,
      head: [["Proveedor", "Órdenes", "Adeudado", "Pagado", "Pendiente"]],
      body: proveedores.map((p) => [ limpiar(p.nombre), p.ordenes, money(p.total), money(p.pagado), p.pendiente > 0 ? money(p.pendiente) : "Pagado" ]),
      columnStyles: {
        1: { halign: "center", cellWidth: 20 },
        2: { halign: "right" },
        3: { halign: "right", textColor: PALETTE.success },
        4: { halign: "right", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "body") {
          const shade = zebraIndex % 2 === 0 ? 255 : 248;
          data.cell.styles.fillColor = [shade, shade, shade];
          if (data.column.index === 4) {
            const val = proveedores[data.row.index]?.pendiente || 0;
            data.cell.styles.textColor = val > 0 ? PALETTE.danger : PALETTE.success;
          }
          if (data.column.index === data.table.columns.length - 1) zebraIndex++;
        }
      }
    });
    y = doc.lastAutoTable.finalY + 4;
    if (resumenProveedoresTexto && resumenProveedoresTexto.length) y = drawSmallText(resumenProveedoresTexto, y) + 4;
  }

  // Paginación
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8.5);
    doc.setTextColor(...PALETTE.grayLabel);
    doc.text(`Página ${i} de ${pages}`, pageW / 2, pageH - 8, { align: "center" });
    doc.text(`Generado: ${new Date().toLocaleString("es-CO")}`, pageW - 14, pageH - 8, { align: "right" });
    doc.text(`Swalquiler - ${emp.nombre || ""}`, 14, pageH - 8);
  }

  const fechaStr = fechaHoy();
  doc.save(`Dashboard_${limpiar(periodoTexto || fechaStr).replace(/\s+/g, "_")}.pdf`);
}

export default generarPDFDashboard;